/**
 * DiffCell Component
 *
 * Table cell component for displaying diff comparisons between actual and expected values.
 * Used in values testing pages to highlight matches, mismatches, and new values.
 */

import { Table, Text, VStack } from '@chakra-ui/react';
import type React from 'react';

/**
 * Compare two values and return diff status
 */
export function compareValues(
  actual: number | boolean | string,
  expected: number | boolean | string | undefined
): 'match' | 'mismatch' | 'new' {
  if (expected === undefined) return 'new';
  if (typeof actual === 'number' && typeof expected === 'number') {
    // Allow small tolerance for floating point
    return Math.abs(actual - expected) < 0.001 ? 'match' : 'mismatch';
  }
  return actual === expected ? 'match' : 'mismatch';
}

/**
 * Format number for display - full dollars with commas
 */
export function formatNumber(value: number): string {
  // Round to nearest dollar and format with commas
  return Math.round(value).toLocaleString('en-US');
}

interface DiffCellProps {
  actual: number | boolean | string;
  expected: number | boolean | string | undefined;
  format?: (v: number | boolean | string) => string;
  /** 'xs' for candidate comparison table (default), 'compact' for cashflow table */
  size?: 'xs' | 'compact';
}

/**
 * Cell component with diff highlighting
 * size="xs" for candidate comparison table (default)
 * size="compact" for cashflow table (extra small 10px)
 */
export const DiffCell: React.FC<DiffCellProps> = ({ actual, expected, format, size = 'xs' }) => {
  const status = compareValues(actual, expected);
  const displayValue = format ? format(actual) : String(actual);
  const expectedDisplay =
    expected !== undefined ? (format ? format(expected) : String(expected)) : '-';

  const bgColor = {
    match: 'green.50',
    mismatch: 'red.50',
    new: 'yellow.50',
  }[status];

  const borderColor = {
    match: 'green.200',
    mismatch: 'red.200',
    new: 'yellow.200',
  }[status];

  // Compact size for cashflows - extra small with tight padding
  const isCompact = size === 'compact';
  const fontSize = isCompact ? '10px' : 'xs';
  const expectedFontSize = isCompact ? '9px' : '2xs';
  const cellPadding = isCompact ? { py: 0.5, px: 1 } : {};

  return (
    <Table.Cell bg={bgColor} borderColor={borderColor} {...cellPadding}>
      <VStack gap={0} align="start">
        <Text fontSize={fontSize} fontWeight="medium" whiteSpace="nowrap">
          {displayValue}
        </Text>
        {status !== 'new' && status !== 'match' && (
          <Text fontSize={expectedFontSize} color="gray.500" whiteSpace="nowrap">
            exp: {expectedDisplay}
          </Text>
        )}
      </VStack>
    </Table.Cell>
  );
};
